import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { ADMIN_SCOPES } from '../../constants/admin_scope_constants';
import { updateAdminScopes, removeAdminAccess } from '../../api/admin_scopes';

import './AdminScopeEditor.scss';

const INTERNAL_EMAIL_DOMAINS = ['shopmy.us', 'shopmyshelf.us'];

const isInternalEmail = email => {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return INTERNAL_EMAIL_DOMAINS.includes(domain);
};

const getPresetsForScopes = (scopeString, presets) => {
  if (!scopeString) return [];
  const scopeList = scopeString.split(',').filter(Boolean);

  if (scopeList.includes(ADMIN_SCOPES.ALL)) {
    return presets.filter(p => p.scopes.includes(ADMIN_SCOPES.ALL)).map(p => p.label);
  }

  return presets
    .filter(p => !p.scopes.includes(ADMIN_SCOPES.ALL))
    .filter(p => p.scopes.every(s => scopeList.includes(s)))
    .map(p => p.label);
};

const AdminScopeEditor = ({ user, presets, onClose, onUpdated }) => {
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const getUserScopes = () => user?.admin_scopes?.scopes || '';

  useEffect(() => {
    if (!user || !presets.length) return;
    setSelectedPresets(getPresetsForScopes(user?.admin_scopes?.scopes || '', presets));
  }, [user, presets]);

  const getScopesForPresets = presetLabels => {
    const scopeSet = new Set();
    for (const label of presetLabels) {
      const preset = presets.find(p => p.label === label);
      if (preset) {
        preset.scopes.forEach(s => scopeSet.add(s));
      }
    }
    return [...scopeSet];
  };

  const togglePreset = preset => {
    setSelectedPresets(prev => {
      const has = prev.includes(preset.label);

      // If toggling Super Admin on, it replaces everything
      if (!has && preset.scopes.includes(ADMIN_SCOPES.ALL)) {
        return [preset.label];
      }

      // If toggling any non-Super-Admin preset on, remove Super Admin
      const withoutSuperAdmin = prev.filter(label => {
        const p = presets.find(pr => pr.label === label);
        return p && !p.scopes.includes(ADMIN_SCOPES.ALL);
      });

      if (has) {
        return prev.filter(l => l !== preset.label);
      }
      return [...withoutSuperAdmin, preset.label];
    });
  };

  const handleSave = async () => {
    if (!selectedPresets.length) {
      cogoToast.warn('Select at least one role, or remove admin access instead.');
      return;
    }

    const scopeList = getScopesForPresets(selectedPresets);
    const currentScopes = getUserScopes()
      .split(',')
      .filter(Boolean);
    const wasSuperAdmin = currentScopes.includes(ADMIN_SCOPES.ALL);
    const willBeSuperAdmin = scopeList.includes(ADMIN_SCOPES.ALL);

    // Block super admin for non-internal emails (client-side enforcement)
    if (willBeSuperAdmin && !isInternalEmail(user.email)) {
      cogoToast.error('Super Admin can only be granted to @shopmy.us or @shopmyshelf.us emails.');
      return;
    }

    // Confirm if promoting to super admin
    if (willBeSuperAdmin && !wasSuperAdmin) {
      const confirmed = window.confirm(
        `Are you sure you want to promote ${user.name || 'this user'} to Super Admin? They will have full access to all admin features.`
      );
      if (!confirmed) return;
    }

    // Confirm if demoting from super admin
    if (wasSuperAdmin && !willBeSuperAdmin) {
      const newRoles = selectedPresets.join(', ');
      const confirmed = window.confirm(
        `Are you sure you want to remove Super Admin from ${user.name || 'this user'}? Their access will be limited to: ${newRoles}.`
      );
      if (!confirmed) return;
    }

    // Confirm for non-internal emails being granted admin
    if (!user.isAdmin && !isInternalEmail(user.email)) {
      const confirmed = window.confirm(
        `Warning: ${user.email} is not an internal ShopMy email. Are you sure you want to grant admin access to this user?`
      );
      if (!confirmed) return;
    }

    setIsSaving(true);
    try {
      await updateAdminScopes(user.id, scopeList.join(','));
      cogoToast.success('Admin scopes updated successfully.');
      onUpdated?.();
      onClose();
    } catch (err) {
      cogoToast.error(err || 'Failed to update scopes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveAdmin = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to remove admin access from ${user.name || 'this user'}? They will no longer be able to access the admin panel.`
    );
    if (!confirmed) return;

    setIsSaving(true);
    try {
      await removeAdminAccess(user.id);
      cogoToast.success('Admin access removed.');
      onUpdated?.();
      onClose();
    } catch (err) {
      cogoToast.error(err || 'Failed to remove admin access.');
    } finally {
      setIsSaving(false);
    }
  };

  const getVisiblePresets = () => {
    if (!isInternalEmail(user?.email)) {
      return presets.filter(p => !p.scopes.includes(ADMIN_SCOPES.ALL));
    }
    return presets;
  };

  if (!user) return null;

  const visiblePresets = getVisiblePresets();
  const isCurrentlyAdmin = !!user.isAdmin;

  return (
    <div className='admin-scope-editor-overlay' onClick={onClose}>
      <div className='admin-scope-editor-panel' onClick={e => e.stopPropagation()}>
        <div className='panel-header'>
          <h2>Admin Scopes</h2>
          <FontAwesomeIcon icon={faTimes} className='close-btn' onClick={onClose} />
        </div>

        <div className='panel-user-info'>
          <span className='user-name'>{user.name || 'Unknown'}</span>
          <span className='user-email'>{user.email}</span>
          {!isInternalEmail(user.email) && <span className='external-badge'>External</span>}
        </div>

        {!presets.length ? (
          <div className='loading'>Loading...</div>
        ) : (
          <div className='panel-body'>
            <div className='preset-list'>
              {visiblePresets.map(preset => {
                const isActive = selectedPresets.includes(preset.label);
                return (
                  <div key={preset.label} className={cn('preset-card', { active: isActive })} onClick={() => togglePreset(preset)}>
                    <div className='preset-header'>
                      <div className={cn('preset-checkbox', { checked: isActive })} />
                      <span className='preset-label'>{preset.label}</span>
                    </div>
                    <p className='preset-description'>{preset.description}</p>
                  </div>
                );
              })}
            </div>

            <div className='panel-actions'>
              <button className='save-btn' onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : isCurrentlyAdmin ? 'Update Scopes' : 'Grant Admin Access'}
              </button>
              {isCurrentlyAdmin && (
                <button className='remove-btn' onClick={handleRemoveAdmin} disabled={isSaving}>
                  Remove Admin Access
                </button>
              )}
              <button className='cancel-btn' onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AdminScopeEditor.propTypes = {
  user: PropTypes.object, // { id, name, email, isAdmin, admin_scopes }
  presets: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func // callback after save/remove to refresh user list
};

export default AdminScopeEditor;
